import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Row, Col, Card, Badge, Button,
  Form, Alert, Spinner, Modal, Table,
} from 'react-bootstrap';
import { getClientDigests, getClientDigestPreview, sendClientDigest } from './DataService';

const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const moisPrecedent = () => {
  const now = new Date();
  const m = now.getMonth(); // 0-11, donc déjà le mois précédent en base 1
  return {
    annee: m === 0 ? now.getFullYear() - 1 : now.getFullYear(),
    mois: m === 0 ? 12 : m,
  };
};

const enPeriode = (annee, mois) => `${annee}-${String(mois).padStart(2, '0')}`;

/**
 * Affiche la satisfaction telle que le client la verra.
 *
 * En dessous de 10 réponses, l'API ne calcule volontairement pas de NPS : sur
 * six réponses un seul détracteur le déplacerait de dix-sept points. L'écran
 * doit refléter ce choix, sinon on relirait un chiffre qui n'apparaît pas dans
 * l'e-mail.
 */
const SatisfactionCell = ({ satisfaction }) => {
  if (!satisfaction || satisfaction.mode === 'aucune') {
    return <span className="text-muted">Aucune réponse</span>;
  }

  if (satisfaction.mode === 'descriptif') {
    return (
      <span>
        <Badge bg="secondary">Sans score</Badge>{' '}
        <small className="text-muted">
          {satisfaction.nb_promoteurs}/{satisfaction.nb_reponses} recommandent
        </small>
      </span>
    );
  }

  const nps = satisfaction.nps;
  const couleur = nps >= 50 ? 'success' : nps >= 0 ? 'warning' : 'danger';

  return (
    <span>
      <Badge bg={couleur}>
        NPS {nps >= 0 ? '+' : ''}{nps}
      </Badge>{' '}
      <small className="text-muted">sur {satisfaction.nb_reponses} réponses</small>
    </span>
  );
};

const ClientDigestsScreen = () => {
  const prec = moisPrecedent();

  const [annee, setAnnee] = useState(prec.annee);
  const [mois, setMois] = useState(prec.mois);

  const [digests, setDigests] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [succes, setSucces] = useState(null);

  const [apercu, setApercu] = useState(null); // { digest, html }
  const [chargementApercu, setChargementApercu] = useState(false);
  const [envoiEnCours, setEnvoiEnCours] = useState(null); // id en cours d'envoi

  const periode = enPeriode(annee, mois);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const data = await getClientDigests(periode);
      setDigests(data.digests || []);
    } catch (e) {
      setErreur("Impossible de récupérer les récapitulatifs.");
    } finally {
      setChargement(false);
    }
  }, [periode]);

  useEffect(() => {
    charger();
  }, [charger]);

  const ouvrirApercu = async (digest) => {
    setChargementApercu(true);
    setErreur(null);
    setApercu({ digest, html: null });
    try {
      const html = await getClientDigestPreview(digest.id);
      setApercu({ digest, html });
    } catch (e) {
      setErreur("Impossible de charger l'aperçu.");
      setApercu(null);
    } finally {
      setChargementApercu(false);
    }
  };

  const envoyer = async (digest) => {
    // Confirmation explicite : c'est un e-mail nominatif vers un client, il n'y
    // a pas de retour en arrière possible une fois parti.
    const ok = window.confirm(
      `Envoyer le récapitulatif de ${MOIS[mois - 1]} ${annee} à ${digest.client} (${digest.email}) ?`
    );
    if (!ok) return;

    setEnvoiEnCours(digest.id);
    setErreur(null);
    setSucces(null);
    try {
      const res = await sendClientDigest(digest.id);
      setSucces(res.message);
      setApercu(null);
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.message || "L'envoi a échoué.");
    } finally {
      setEnvoiEnCours(null);
    }
  };

  const brouillons = digests.filter((d) => d.statut === 'brouillon');
  const envoyes = digests.filter((d) => d.statut === 'envoye');
  const anneeActuelle = new Date().getFullYear();

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 className="mb-1">Récaps mensuels clients</h3>
          <p className="text-muted mb-0">
            Générés par <code>digest:generate-client-monthly</code>. Rien ne part automatiquement :
            chaque envoi est déclenché ici, après relecture.
          </p>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col xs={6} md={3}>
              <Form.Label>Mois</Form.Label>
              <Form.Select value={mois} onChange={(e) => setMois(Number(e.target.value))}>
                {MOIS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Label>Année</Form.Label>
              <Form.Select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
                {[anneeActuelle, anneeActuelle - 1, anneeActuelle - 2].map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Button variant="outline-primary" onClick={charger} disabled={chargement}>
                {chargement ? 'Chargement…' : 'Rafraîchir'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {erreur && <Alert variant="danger" onClose={() => setErreur(null)} dismissible>{erreur}</Alert>}
      {succes && <Alert variant="success" onClose={() => setSucces(null)} dismissible>{succes}</Alert>}

      {chargement ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : digests.length === 0 ? (
        <Alert variant="info">
          Aucun récapitulatif pour {MOIS[mois - 1]} {annee}. Lancer{' '}
          <code>php artisan digest:generate-client-monthly --month={periode}</code> pour les générer.
        </Alert>
      ) : (
        <>
          <div className="mb-2 text-muted">
            {brouillons.length} brouillon{brouillons.length > 1 ? 's' : ''} ·{' '}
            {envoyes.length} envoyé{envoyes.length > 1 ? 's' : ''}
          </div>

          <Card>
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Client</th>
                  <th className="text-center">Livraisons</th>
                  <th className="text-end">Montant TTC</th>
                  <th>Satisfaction</th>
                  <th className="text-center">Verbatims</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {digests.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div className="fw-semibold">{d.client}</div>
                      <small className="text-muted">{d.email}</small>
                    </td>
                    <td className="text-center">{d.nb_livraisons}</td>
                    <td className="text-end">
                      {Number(d.montant_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                    </td>
                    <td><SatisfactionCell satisfaction={d.satisfaction} /></td>
                    <td className="text-center">{d.nb_verbatims}</td>
                    <td>
                      {d.statut === 'envoye' ? (
                        <>
                          <Badge bg="success">Envoyé</Badge>
                          <div>
                            <small className="text-muted">
                              {d.sent_at ? new Date(d.sent_at).toLocaleString('fr-FR') : ''}
                            </small>
                          </div>
                        </>
                      ) : (
                        <Badge bg="secondary">Brouillon</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => ouvrirApercu(d)}
                      >
                        Aperçu
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={d.statut === 'envoye' || envoiEnCours === d.id}
                        onClick={() => envoyer(d)}
                      >
                        {envoiEnCours === d.id ? 'Envoi…' : 'Envoyer'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <Modal show={!!apercu} onHide={() => setApercu(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">
            Aperçu — {apercu?.digest?.client}
            <div><small className="text-muted fw-normal">{apercu?.digest?.email}</small></div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: '#eff2f7' }}>
          {chargementApercu || !apercu?.html ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            // srcDoc et non src : l'endpoint exige un en-tête Authorization
            // qu'une iframe ne peut pas envoyer. sandbox vide le HTML de tout
            // script — un aperçu n'a aucune raison d'exécuter du code.
            <iframe
              title="Aperçu du récapitulatif"
              srcDoc={apercu.html}
              sandbox=""
              style={{ width: '100%', height: '70vh', border: 0, display: 'block' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setApercu(null)}>Fermer</Button>
          {apercu?.digest?.statut !== 'envoye' && (
            <Button
              variant="primary"
              disabled={envoiEnCours === apercu?.digest?.id}
              onClick={() => envoyer(apercu.digest)}
            >
              {envoiEnCours === apercu?.digest?.id ? 'Envoi…' : 'Envoyer à ce client'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientDigestsScreen;
